import {
  isSomething,
  epochToViewUTC,
  epochToView,
  DataRecurringRule,
  createdBySystemNameMappings,
  asUTCEpoch,
  formatDate,
} from '@tokeet-frontend/tv3-platform'
import * as moment from 'moment'
import * as lodash from 'lodash'

export const defaultTaskList = $localize`My Tasks`

export const defaultTaskColors = [
  { color: '#F8D004', bg: '#FFFCF3' },
  { color: '#8FC901', bg: '#F0F9F1' },
  { color: '#09B44A', bg: '#ddefe1' },
  { color: '#00CDCD', bg: '#f0f8ff' },
  { color: '#3470EA', bg: '#DDEFFF' },
  { color: '#7B57D4', bg: '#FBEEFF' },
]

export function getTaskBgColor(color: string) {
  return defaultTaskColors.find((c) => c.color === color)?.bg
}

export enum TaskLegacyStatus {
  Pending = 1,
  Completed = 2,
}

export enum TaskStatus {
  Pending = 'pending',
  InProgress = 'inprogress',
  DueToday = 'due-today',
  PastDue = 'pastdue',
  Completed = 'completed',
  Canceled = 'canceled',
  Rejected = 'rejected',
  NeedsHelp = 'needshelp',
}

export const taskStatusOptions = [
  { value: TaskStatus.Canceled, label: $localize`Canceled`, color: 'light' },
  { value: TaskStatus.Completed, label: $localize`Completed`, color: 'success' },
  { value: TaskStatus.DueToday, label: $localize`Due Today`, calculated: true, color: 'warning' },
  { value: TaskStatus.InProgress, label: $localize`In Progress`, color: 'info' },
  { value: TaskStatus.NeedsHelp, label: $localize`Needs Help`, color: 'indigo' },
  { value: TaskStatus.PastDue, label: $localize`Past Due`, color: 'danger' },
  { value: TaskStatus.Pending, label: $localize`Pending`, color: 'primary' },
  { value: TaskStatus.Rejected, label: $localize`Rejected`, color: 'light' },
]

export function getTaskStatusOptions(includeCalculated = false) {
  return taskStatusOptions.filter((option) => includeCalculated || !option.calculated)
}

export function getTaskStatusLabel(status: TaskStatus) {
  return taskStatusOptions.find((option) => option.value === status)?.label
}

export function getTaskStatusColor(status: TaskStatus) {
  return taskStatusOptions.find((option) => option.value === status)?.color
}

export interface TaskResponse extends DataRecurringRule {
  pkey: string
  account: number
  name: string

  list?: string
  status: TaskStatus | TaskLegacyStatus
  notes: string
  draft?: 0 | 1 // indicate this is a draft task.

  timezone?: string
  start: number
  due: number
  marked?: number
  color?: string
  tags?: string[]
  attachments?: string[]

  user_ids: string[]
  user_id: string
  inquiry_id?: string
  rental_id?: string
  created_by?: string
  trigger_id?: string
  incident_id?: string

  events?: string // Automated Task

  checklist?: TaskChecklistItem[]

  created: number
  updated?: number
  archived: number
}

export interface TaskChecklistItem {
  name: string
  description?: string
  status?: number
  date_completed?: number // epoch
  completed_by?: string // user id
}

export type CreateTaskPayload = Omit<TaskResponse, 'pkey' | 'created' | 'account' | 'archived' | 'list' | 'user_id'>
export type UpdateTaskPayload = Partial<CreateTaskPayload & Pick<TaskResponse, 'list'>>
export interface FetchTasksParams {
  name?: string
  status?: TaskStatus[]
  pkeys?: string[]
  inquiries?: string[]
  trigger_id?: string
  incident_id?: string
  users?: string[]
  rentals?: string[]
  due_from?: number
  due_to?: number
  unarchived?: boolean
  limit?: number
  skip?: number
}

export interface TaskView extends TaskResponse {
  status: TaskStatus
  startView: string
  dueView: string
  createdView: string
  updatedView: string
  bgColor?: string

  isAuto?: boolean

  createdByView?: string
  usersView?: string
  inquiryView?: string
  rentalView?: string
  incidentView?: string
}

function epochToTaskTimezoneView(field: number, timezone?: string) {
  if (!timezone || !moment.tz.zone(timezone)) return epochToViewUTC(field, 'datetime')

  return formatDate(moment.unix(+field).tz(timezone), 'datetime')
}

export function upgradeTaskStatus(task: TaskResponse, noCalculated = false): TaskStatus {
  // upgrade status
  let isLegacyStatus = false
  if (task.status == TaskLegacyStatus.Completed || task.status == TaskLegacyStatus.Pending || !task.status) {
    isLegacyStatus = true
  }
  if (!isLegacyStatus) return task.status as TaskStatus

  const nowEpoch = asUTCEpoch(new Date()) as number

  const past = task.status !== TaskLegacyStatus.Completed && nowEpoch > task.due
  const pending = task.status !== TaskLegacyStatus.Completed && !(nowEpoch > task.due)

  if (task.status === TaskLegacyStatus.Completed) {
    return TaskStatus.Completed
  } else if (moment.utc().isSame(task.due * 1000, 'date') && !noCalculated) {
    return TaskStatus.DueToday
  } else if (nowEpoch > task.start && nowEpoch < task.due) {
    return TaskStatus.InProgress
  } else if (past) {
    return TaskStatus.PastDue
  }

  return TaskStatus.Pending
}

export function taskToView(task: TaskResponse): TaskView {
  const color = task.color || defaultTaskColors[0].color

  const view: TaskView = {
    ...task,
    color,
    status: upgradeTaskStatus(task),
    due: +task.due,
    start: +task.start,
    repeat: lodash.isNil(task.repeat) ? 0 : +task.repeat,
    user_ids: (isSomething(task.user_ids) ? task.user_ids : [task.user_id]).filter((t) => !!t),
    // views
    bgColor: getTaskBgColor(color),
    isAuto: !!createdBySystemNameMappings[task.created_by],
    startView: epochToTaskTimezoneView(task.start, task.timezone),
    dueView: epochToTaskTimezoneView(task.due, task.timezone),
    createdView: epochToView(task.created),
    updatedView: task.updated ? epochToTaskTimezoneView(task.updated, task.timezone) : '',
  }

  return view
}
